import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getRemoteConfig,
  RemoteConfig,
  fetchAndActivate,
  getString,
  getBoolean,
} from 'firebase/remote-config';
import {
  RemoteConfigDefaults,
  ListKeys,
  BooleanKeys,
  StringKeys,
  isBooleanKey,
  isListKey,
  type BooleanKeyType,
  type StringKeyType,
  type ListKeyType,
} from './remote-config-keys';

// Re-export keys for convenience
export { RemoteConfigKeys, ListKeys, BooleanKeys, StringKeys } from './remote-config-keys';

// Firebase configuration based on environment
const getFirebaseConfig = () => {
  const env = import.meta.env.VITE_ENV || 'development';

  // Common config values
  const commonConfig = {
    authDomain: 'centrajob-6d384.firebaseapp.com',
    projectId: 'centrajob-6d384',
    storageBucket: 'centrajob-6d384.appspot.com',
    messagingSenderId: '196333563994',
    apiKey: 'AIzaSyCQ3k1chNNLZ2vRQesEyA5pYsVBNtcERLs',
  };

  // Environment-specific appId
  const appIds: Record<string, string> = {
    development: '1:196333563994:web:05e8d24277c24d98dde863',
    qa: '1:196333563994:web:4735176726df42b1dde863',
    production: '1:196333563994:web:624ce618b1074de8dde863',
  };

  return {
    ...commonConfig,
    appId: appIds[env] || appIds.development,
  };
};

// Initialize Firebase
let app: FirebaseApp;
let remoteConfig: RemoteConfig;
let isInitialized = false;

export const initializeFirebase = () => {
  if (!isInitialized) {
    app = initializeApp(getFirebaseConfig());
    remoteConfig = getRemoteConfig(app);

    // Set minimum fetch interval (for development, use shorter interval)
    remoteConfig.settings.minimumFetchIntervalMillis =
      import.meta.env.VITE_ENV === 'production' ? 3600000 : 60000; // 1 hour for prod, 1 min for dev

    // Set default values from remote-config-keys.ts
    remoteConfig.defaultConfig = RemoteConfigDefaults;

    isInitialized = true;
  }
  return { app, remoteConfig };
};

// Fetch and activate remote config
export const fetchRemoteConfig = async (): Promise<boolean> => {
  try {
    const { remoteConfig } = initializeFirebase();
    const activated = await fetchAndActivate(remoteConfig);
    console.log('Remote config fetched and activated:', activated);
    return activated;
  } catch (error) {
    console.error('Error fetching remote config:', error);
    return false;
  }
};

// ==================== Typed Getter Functions ====================

// Get boolean value from remote config
export const getRemoteConfigBoolean = (key: BooleanKeyType): boolean => {
  try {
    const { remoteConfig } = initializeFirebase();
    return getBoolean(remoteConfig, key);
  } catch (error) {
    console.error(`Error getting boolean for key ${key}:`, error);
    return (RemoteConfigDefaults[key] as boolean) ?? false;
  }
};

// Get string value from remote config
export const getRemoteConfigString = (key: StringKeyType | ListKeyType): string => {
  try {
    const { remoteConfig } = initializeFirebase();
    return getString(remoteConfig, key);
  } catch (error) {
    console.error(`Error getting string for key ${key}:`, error);
    return (RemoteConfigDefaults[key] as string) ?? '';
  }
};

// Get list value from remote config (JSON string to array)
export const getRemoteConfigList = (key: ListKeyType): string[] => {
  try {
    const jsonString = getRemoteConfigString(key);
    if (!jsonString || jsonString === '[]') return [];
    return JSON.parse(jsonString) as string[];
  } catch (error) {
    console.error(`Error parsing remote config list for key ${key}:`, error);
    return [];
  }
};

// Get any value from remote config (auto-detect type)
export const getRemoteConfigValue = (key: string): boolean | string | string[] => {
  try {
    const { remoteConfig } = initializeFirebase();

    if (isBooleanKey(key)) {
      return getBoolean(remoteConfig, key);
    }

    if (isListKey(key)) {
      const jsonString = getString(remoteConfig, key);
      if (!jsonString || jsonString === '[]') return [];
      return JSON.parse(jsonString) as string[];
    }

    // Default to string
    return getString(remoteConfig, key);
  } catch (error) {
    console.error(`Error getting value for key ${key}:`, error);
    return RemoteConfigDefaults[key] ?? '';
  }
};

// ==================== Convenience Functions ====================

// Check if a feature is enabled
export const isFeatureEnabled = (key: BooleanKeyType): boolean => {
  return getRemoteConfigBoolean(key);
};

// Get all learning material config values
export const getLearningMaterialConfig = () => {
  return {
    categoryList: getRemoteConfigList(ListKeys.learningMaterialCategoryList),
    tagsList: getRemoteConfigList(ListKeys.learningMaterialTagsList),
    typeList: getRemoteConfigList(ListKeys.learningMaterialDocumentTypeList),
    statusList: getRemoteConfigList(ListKeys.learningMaterialStatusList),
    batchLevelList: getRemoteConfigList(ListKeys.learningMaterialBatchLevelList),
  };
};

// Get all job type config values
export const getJobTypeConfig = () => {
  return {
    jobTypeList: getRemoteConfigList(ListKeys.v2JobTypeList),
  };
};

// Get WhatsApp share message config
export const getWhatsAppShareConfig = () => {
  return {
    title: getRemoteConfigString(StringKeys.whatsappShareMessageTitle),
    subTitle: getRemoteConfigString(StringKeys.whatsappShareMessageSubTitle),
    playstoreLink: getRemoteConfigString(StringKeys.whatsappShareMessageAppPlaystoreLink),
    iosLink: getRemoteConfigString(StringKeys.whatsappShareMessageAppIosLink),
    webUrl: getRemoteConfigString(StringKeys.whatsappShareMessageCandidateWebUrl),
    isEnabled: getRemoteConfigBoolean(BooleanKeys.enableShareWhatsappMessageButton),
  };
};
